import { Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { InitStep } from '@/hooks/use-app-init';

interface LoadingSpinnerProps {
  size?: number;
  className?: string;
}

export function LoadingSpinner({ size = 24, className = '' }: LoadingSpinnerProps) {
  return (
    <Loader2
      size={size}
      className={`animate-spin text-primary ${className}`}
    />
  );
}

interface LoadingScreenProps {
  message?: string;
  currentStep?: InitStep;
}

const INIT_STEPS: { key: InitStep; label: string }[] = [
  { key: 'parsing', label: 'Configuration' },
  { key: 'modules', label: 'Loading modules' },
  { key: 'auth', label: 'Authentication' },
  { key: 'org', label: 'Organization' },
];

export function LoadingScreen({ message = 'Loading...', currentStep }: LoadingScreenProps) {
  // If no step info, show simple loading
  if (!currentStep) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size={40} />
          <p className="text-sm text-muted-foreground animate-pulse">{message}</p>
        </div>
      </div>
    );
  }

  const currentIdx = INIT_STEPS.findIndex((s) => s.key === currentStep);
  // Progress percentage based on completed steps
  const progress = currentStep === 'done'
    ? 100
    : Math.max(5, Math.round(((currentIdx >= 0 ? currentIdx : 0) / INIT_STEPS.length) * 100));

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 w-72">
        {/* Spinner */}
        <LoadingSpinner size={36} />

        {/* Progress bar */}
        <div className="w-full">
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step list */}
        <div className="w-full space-y-2">
          {INIT_STEPS.map((step, idx) => {
            const isCompleted = currentIdx > idx || currentStep === 'done';
            const isCurrent = currentIdx === idx && currentStep !== 'done';

            return (
              <div
                key={step.key}
                className={cn(
                  'flex items-center gap-2.5 text-xs transition-all duration-300',
                  isCompleted && 'text-muted-foreground',
                  isCurrent && 'text-foreground font-medium',
                  !isCompleted && !isCurrent && 'text-muted-foreground/50'
                )}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                ) : isCurrent ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" />
                ) : (
                  <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30 shrink-0" />
                )}
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function LoadingOverlay({ message }: { message?: string }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size={32} />
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
}
